// Client-side booking total for the embedded booking widget (and the client / Guide Profile
// checkouts that share it). Moved out of Embedded/ExperienceBooking/BookingDetailCard so the new
// widget prices bookings exactly like the old one. Pure functions only: no component state.
//
// Breakdown lines keep their original `label` / `amount` strings (the old widget renders them as
// is) and also carry `kind`, `count` and a numeric `value` so other screens can word them their own
// way. kind: "flat_rate" | "extra_adult" | "extra_child" | "extra_infant" | "adult" | "child" |
// "infant" | "passenger" | "ticket" | "add_on" | "fee" | "tax_and_fees" | "card_processing_fee".
import dayjs from "dayjs"
import { TRIP_PRICE_RATE_BASIS } from "constants/experiences"
import { numberToCurrency } from "helpers/string"
import { computeDiscountAmount } from "utils/discountUtils"
import { summarizeAddOnFees } from "components/bookings/helpers"
import { getCardFixedFee, roundToCents } from "utils/bookingCalculations"
import {
  applyPriceAdjustments,
  getApplicableDayAdjustments,
  getApplicableSeasonalAdjustments,
  PROGRESSIVE_UNIT
} from "utils/calculateBookingTotal"
import {
  DURATION_MODES,
  DURATION_TYPES,
  enumerateDates,
  getProgressiveAdjustment,
  isDynamicMultiDay,
  isMultiDayTrip,
  priceUnitForBooking
} from "utils/multiDayPricing"

const EMPTY_COUNTS = { adults: 0, children: 0, infants: 0, passengers: 0 }

const PROGRESSIVE_PRICING_FIELDS = [
  "progressive_pricing_enabled",
  "progressive_pricing_threshold",
  "progressive_pricing_direction",
  "progressive_pricing_value",
  "progressive_pricing_unit",
  "progressive_pricing_cap"
]

// The experience's progressive duration pricing (a dynamic trip's price per day changing after a
// number of days), passed with its day and seasonal adjustments.
export const getProgressivePricing = (experience) => Object.fromEntries(PROGRESSIVE_PRICING_FIELDS.map((field) => [field, experience?.[field]]))

/**
 * How a multi-day booking is priced: the dates it covers, whether its trip price is a rate per day
 * (a dynamic trip, or a fixed duration sold per day) or one price for the whole trip, and whether
 * the trip is dynamic (progressive pricing applies). Null for a single-day booking, and for a fixed
 * trip price without a length (older records), which are priced on their start date as before.
 * A dynamic trip without its end date yet covers its start date only.
 *
 * @returns {{ dates: string[], isPerDay: boolean, isDynamic: boolean } | null}
 */
export const getMultiDayBooking = ({ experience, priceData, date, endDate }) => {
  if (!isMultiDayTrip(experience) || !date) return null
  if (isDynamicMultiDay(experience)) return { dates: enumerateDates(date, endDate || date), isPerDay: true, isDynamic: true }
  const days = Number(priceData?.duration_days) || 0
  if (!days) return null
  const lastDate = dayjs(date).add(days - 1, "day").format("YYYY-MM-DD")
  return { dates: enumerateDates(date, lastDate), isPerDay: priceData?.rate_basis === TRIP_PRICE_RATE_BASIS.PER_DAY, isDynamic: false }
}

const buildLine = ({ kind, label, value, count, ...rest }) => ({
  kind,
  label,
  amount: numberToCurrency(value),
  value,
  ...(count != null && { count }),
  ...rest
})

const getApplicableAdjustments = ({ dayAdjustments, seasonalAdjustments, availableDateRanges, date }) => [
  ...getApplicableDayAdjustments(dayAdjustments, date),
  ...getApplicableSeasonalAdjustments(seasonalAdjustments, date, availableDateRanges)
]

const computeAdjustedRates = (priceData, applicableAdjustments) => {
  const { flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger } = priceData
  const adjustPrice = (price) => applyPriceAdjustments(price, applicableAdjustments)
  return {
    flatAmount: adjustPrice(flat_rate),
    pricePerPerson: adjustPrice(price_per_person),
    pricePerChild: adjustPrice(price_per_child),
    pricePerInfant: adjustPrice(price_per_infant),
    pricePerPassenger: adjustPrice(price_per_passenger)
  }
}

// The trip a multi-day booking is priced against: the adjustments it was given (none for the price
// before adjustments) and, for a dynamic trip, its progressive pricing.
const buildMultiDayPricingTrip = ({ multiDay, dayAdjustments, seasonalAdjustments, availableDateRanges, progressivePricing }) => ({
  duration_type: DURATION_TYPES.MULTI_DAY,
  duration_mode: multiDay.isDynamic ? DURATION_MODES.DYNAMIC : DURATION_MODES.FIXED,
  day_adjustments: dayAdjustments,
  seasonal_adjustments: seasonalAdjustments,
  available_date_ranges: availableDateRanges,
  ...progressivePricing
})

// A multi-day booking's unit prices for the whole trip: a rate per day is charged once per day,
// each day with its own day/seasonal adjustments and (dynamic trips) its progressive step; a price
// for the whole trip takes the start date's adjustments (priceUnitForBooking).
const computeMultiDayRates = (priceData, multiDay, pricingTrip) => {
  const { flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger } = priceData
  const priceUnit = (rate) => priceUnitForBooking(rate, pricingTrip, multiDay.dates, multiDay.isPerDay).total
  return {
    flatAmount: priceUnit(flat_rate),
    pricePerPerson: priceUnit(price_per_person),
    pricePerChild: priceUnit(price_per_child),
    pricePerInfant: priceUnit(price_per_infant),
    pricePerPassenger: priceUnit(price_per_passenger)
  }
}

// Unit prices of one trip price on `date` after its day and seasonal adjustments — the same rates
// calculateWidgetBookingTotal bills with. A multi-day booking (`multiDay`, getMultiDayBooking) gets
// its prices for the whole trip. { flatAmount, pricePerPerson, pricePerChild, pricePerInfant,
// pricePerPassenger }
export const getAdjustedRates = ({ priceData = {}, date, dayAdjustments, seasonalAdjustments, availableDateRanges, multiDay, progressivePricing }) =>
  multiDay
    ? computeMultiDayRates(priceData, multiDay, buildMultiDayPricingTrip({ multiDay, dayAdjustments, seasonalAdjustments, availableDateRanges, progressivePricing }))
    : computeAdjustedRates(priceData, getApplicableAdjustments({ dayAdjustments, seasonalAdjustments, availableDateRanges, date }))

// A fixed-amount ($) progressive step is per guest per stepped day, on the whole day's charge — it
// can't sit in each guest type's unit price (it would be multiplied by that type's count), so it is
// added once here, never taking a day below nothing. Percent steps are in the unit prices already.
// From the old widget (computeProgressiveFixedDeltaTotal), which follows the backend's
// TripBooking#progressive_pricing_delta.
const computeProgressiveFixedDelta = ({ pricingTrip, priceData, multiDay, totalCounts }) => {
  if (!multiDay?.isPerDay || !multiDay.isDynamic || pricingTrip?.progressive_pricing_unit !== PROGRESSIVE_UNIT.FIXED) return 0
  const aggregatePerDayRate =
    priceData?.pricing_type === "charge_flat_rate"
      ? Number(priceData?.flat_rate) || 0
      : (totalCounts.adults || 0) * (Number(priceData?.price_per_person) || 0) +
        (totalCounts.children || 0) * (Number(priceData?.price_per_child) || 0) +
        (totalCounts.infants || 0) * (Number(priceData?.price_per_infant) || 0) +
        (totalCounts.passengers || 0) * (Number(priceData?.price_per_passenger) || 0)
  if (!aggregatePerDayRate) return 0
  const guestCount = (totalCounts.adults || 0) + (totalCounts.children || 0) + (totalCounts.infants || 0) + (totalCounts.passengers || 0)
  return multiDay.dates.reduce((sum, _date, index) => {
    const { flatDelta } = getProgressiveAdjustment(pricingTrip, index + 1, guestCount)
    return flatDelta ? sum + Math.max(flatDelta, -aggregatePerDayRate) : sum
  }, 0)
}

// The fixed-amount progressive step spread over the guest lines in proportion to each line, cent
// remainder on the largest (no line of its own) — as the old widget and the backend's
// TripBooking#distribute_progressive_delta do.
const foldProgressiveDeltaIntoBreakdown = (breakdown, progressiveDelta) => {
  const baseSum = breakdown.reduce((sum, line) => sum + (line.value || 0), 0)
  if (!progressiveDelta || baseSum <= 0) return breakdown
  let remainder = progressiveDelta
  const updated = breakdown.map((line) => {
    if (!line.value) return line
    const share = roundToCents(progressiveDelta * (line.value / baseSum))
    const value = Math.max(0, roundToCents(line.value + share))
    remainder = roundToCents(remainder - (value - line.value))
    return { ...line, value, amount: numberToCurrency(value) }
  })
  if (Math.abs(remainder) >= 0.01) {
    const largest = updated.reduce((best, line) => ((line.value || 0) > (best?.value || 0) ? line : best), null)
    if (largest) {
      largest.value = Math.max(0, roundToCents(largest.value + remainder))
      largest.amount = numberToCurrency(largest.value)
    }
  }
  return updated
}

// Splits guestsIncluded capacity across adults -> children -> infants (in that priority order) and
// returns each group's "extra" (billed) count once the included capacity is exhausted.
export const allocateIncludedCapacity = (totalCounts, guestsIncluded) => {
  let remainingCapacity = guestsIncluded
  const adultsIncluded = Math.min(totalCounts.adults, remainingCapacity)
  remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)
  const childrenIncluded = Math.min(totalCounts.children, remainingCapacity)
  remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)
  const infantsIncluded = Math.min(totalCounts.infants, remainingCapacity)
  return {
    extraAdults: Math.max(0, totalCounts.adults - adultsIncluded),
    extraChildren: Math.max(0, totalCounts.children - childrenIncluded),
    extraInfants: Math.max(0, totalCounts.infants - infantsIncluded)
  }
}

const computeFlatRateBreakdown = ({ totalCounts, rates, includedGuests }) => {
  const { flatAmount, pricePerPerson, pricePerChild, pricePerInfant } = rates
  const totalGuests = totalCounts.adults + totalCounts.children + totalCounts.infants + totalCounts.passengers
  const hasGuests = totalGuests > 0 || totalCounts.passengers > 0
  if (!hasGuests) return { total: 0, breakdown: [] }

  let total = flatAmount
  const breakdown = [
    buildLine({
      kind: "flat_rate",
      label: "Flat Rate",
      value: flatAmount,
      count: Math.min(totalGuests, includedGuests),
      pricingDetails: {
        pricing_type: "charge_flat_rate",
        included_guests: includedGuests,
        price_per_person: pricePerPerson,
        price_per_child: pricePerChild,
        price_per_infant: pricePerInfant
      }
    })
  ]

  const { extraAdults, extraChildren, extraInfants } = allocateIncludedCapacity(totalCounts, includedGuests)

  // Extra guests of every type are billed at the per-person rate.
  const extraLines = [
    { kind: "extra_adult", label: "Extra Adult", count: extraAdults },
    { kind: "extra_child", label: "Extra Child", count: extraChildren },
    { kind: "extra_infant", label: "Extra Infant", count: extraInfants },
    { kind: "passenger", label: "Passenger", count: totalCounts.passengers }
  ]
  for (const { kind, label, count } of extraLines) {
    if (count > 0) {
      const lineTotal = count * pricePerPerson
      total += lineTotal
      breakdown.push(buildLine({ kind, label: `${label} x ${count}`, value: lineTotal, count }))
    }
  }

  return { total, breakdown }
}

const computeCountBreakdown = ({ totalCounts, rates }) => {
  const mapping = [
    { kind: "adult", count: totalCounts.adults, label: "Adult", price: rates.pricePerPerson },
    { kind: "child", count: totalCounts.children, label: "Child", price: rates.pricePerChild },
    { kind: "infant", count: totalCounts.infants, label: "Infant", price: rates.pricePerInfant },
    { kind: "passenger", count: totalCounts.passengers, label: "Passenger", price: rates.pricePerPassenger }
  ]
  let total = 0
  const breakdown = []
  for (const { kind, count, label, price } of mapping) {
    if (count > 0) {
      const lineTotal = count * price
      total += lineTotal
      breakdown.push(buildLine({ kind, label: `${label} x ${count}`, value: lineTotal, count }))
    }
  }
  return { total, breakdown }
}

const computeGuestPricingBreakdown = ({ pricingType, totalCounts, rates, includedGuests }) => {
  if (pricingType === "charge_flat_rate") {
    return computeFlatRateBreakdown({ totalCounts, rates, includedGuests })
  }
  return computeCountBreakdown({ totalCounts, rates })
}

const computeAddOnBreakdown = (addOnSelections, tripAddOns) => {
  let addOnsTotal = 0
  const breakdown = []
  for (const addOn of tripAddOns) {
    const qty = Number(addOnSelections[addOn.id] || 0)
    if (qty > 0) {
      const lineTotal = qty * Number(addOn.price ?? 0)
      addOnsTotal += lineTotal
      breakdown.push(buildLine({ kind: "add_on", label: `${addOn.title} × ${qty}`, value: lineTotal, count: qty, addOnId: addOn.id, title: addOn.title }))
    }
  }
  return { addOnsTotal, breakdown }
}

const computeDiscountTotal = ({ discountPromos, guestSubtotal, addOnsTotal }) => {
  const rawDiscount = (discountPromos ?? []).reduce((sum, promo) => sum + computeDiscountAmount(promo, guestSubtotal, addOnsTotal), 0)
  return Math.max(0, Math.min(rawDiscount, guestSubtotal + addOnsTotal))
}

// An inactive fee entry is left out everywhere, as resolveCardFeeConfig does, so totals and the
// card charge agree.
const resolveFeeEntries = (allFees, cardFeePassthroughEnabled) => {
  const customFees = allFees.filter((fee) => fee?.is_active !== false)
  return {
    cardFeeEntry: cardFeePassthroughEnabled ? customFees.find((fee) => fee.title === "Card Processing Fee") : null,
    platformFeeEntry: customFees.find((fee) => fee.title === "Platform Fee"),
    otherFees: customFees.filter((fee) => fee.title !== "Card Processing Fee" && fee.title !== "Platform Fee")
  }
}

// Platform fee (commission): guest portion at the standard rate + per-add-on commission (each at
// its own commission_percentage, or the standard rate when null).
const computePlatformFeeAmount = ({ platformFeeEntry, guestSubtotal, discountFactor, commissionAddOns }) => {
  if (!platformFeeEntry) return 0
  const platformRate = Number(platformFeeEntry.price) || 0
  const guestPortion = (guestSubtotal * discountFactor * platformRate) / 100
  const addOnPortion = commissionAddOns.reduce((sum, addOn) => {
    const rate = addOn.commissionPercentage != null ? addOn.commissionPercentage : platformRate
    return sum + (addOn.amount * discountFactor * rate) / 100
  }, 0)
  return guestPortion + addOnPortion
}

// Other custom fees: percentage fees apply to the guest subtotal plus opted-in add-ons; fixed fees
// stay flat (charged once).
const buildOtherFeeItems = ({ otherFees, customFeeBase }) =>
  otherFees.map((fee) => ({
    fee,
    amount: fee.fee_type === "percentage" ? (customFeeBase * Number(fee.price)) / 100 : Number(fee.price)
  }))

// Card processing fee applied last. It's charged only on the amount paid by card NOW: the deposit
// (depositFraction of the pre-card total) for deposit payments, or the whole pre-card total when
// paying in full. `total` at this point is the pre-card total.
// Gross-up: divide by (1 - rate) so that after Stripe deducts its fee on (depositBase + fee), the
// net equals depositBase. A plain markup would under-collect by the fee-on-fee. `cardFeeShare` is
// this booking's share of the entry's fixed fee (getFixedCardFeeShares).
const computeCardProcessingFee = ({ cardFeeEntry, total, depositFraction, cardFeeShare }) => {
  if (!cardFeeEntry || total <= 0) return 0
  const cardRate = Number(cardFeeEntry.price) / 100
  const depositBaseForFee = total * depositFraction
  return (depositBaseForFee * cardRate + getCardFixedFee(cardFeeEntry) * cardFeeShare) / (1 - cardRate)
}

// `isCardFeeListed` false: the processing fee stays out of the list (and out of "Tax & Fees"), for a
// screen that shows it below the Total.
const buildFeesBreakdown = ({ hiddenFeesTotal, cardFeeEntry, cardProcessingFee, isCardFeeListed }) => {
  const entries = []
  const listedCardFee = isCardFeeListed ? cardProcessingFee : 0
  const taxAndFeesTotal = hiddenFeesTotal + (cardFeeEntry && !cardFeeEntry.show_as_line_item ? listedCardFee : 0)
  if (taxAndFeesTotal > 0) {
    entries.push(buildLine({ kind: "tax_and_fees", label: "Tax & Fees", value: taxAndFeesTotal }))
  }
  if (cardFeeEntry?.show_as_line_item && listedCardFee > 0) {
    entries.push(buildLine({ kind: "card_processing_fee", label: "Card Processing Fee", value: listedCardFee }))
  }
  return entries
}

// Everything after the guests are priced, shared by private and ticketed bookings: add-ons, the
// promo discount, the custom fees and taxes, and the card processing fee.
const addAddOnsDiscountAndFees = ({
  guestTotal,
  guestBreakdown,
  addOnSelections,
  tripAddOns,
  customFees,
  discountPromos,
  cardFeePassthroughEnabled,
  depositFraction,
  cardFeeShare,
  isCardFeeListed
}) => {
  let total = guestTotal
  const breakdown = [...guestBreakdown]

  // Guest-only subtotal (used as discount base when promo does NOT apply to add-ons)
  const guestSubtotal = total

  const { addOnsTotal, breakdown: addOnBreakdown } = computeAddOnBreakdown(addOnSelections || {}, tripAddOns || [])
  total += addOnsTotal
  breakdown.push(...addOnBreakdown)

  // Apply the discount to the pre-tax base BEFORE computing tax & fees, matching the backend.
  // Each promo's apply_to_add_ons flag decides whether add-ons are part of its discountable base.
  const discountAmount = computeDiscountTotal({ discountPromos, guestSubtotal, addOnsTotal })
  total -= discountAmount

  // Spread the discount across every taxable portion so each fee base is reduced proportionally
  // even though add-on portions can now be taxed at different rates.
  const grossBase = guestSubtotal + addOnsTotal
  const discountFactor = grossBase > 0 ? (grossBase - discountAmount) / grossBase : 1
  const netTaxableBase = grossBase - discountAmount

  // Per-add-on fee participation: the platform fee (commission_applicable/commission_percentage)
  // and the custom percentage fees (apply_custom_tax_and_fees). The card processing fee always
  // applies to every add-on (it's charged on the whole running total below).
  const { customTaxableAddOnsTotal, commissionAddOns } = summarizeAddOnFees(addOnSelections || {}, tripAddOns || [])

  let cardProcessingFee = 0

  // "Card Processing Fee" is applied last on the running total. The "Platform Fee" entry is the
  // standard commission and is charged on the guest subtotal plus any commission-applicable
  // add-ons. All other custom fees include only add-ons that opted in.
  if (netTaxableBase > 0 && customFees?.length > 0) {
    const { cardFeeEntry, platformFeeEntry, otherFees } = resolveFeeEntries(customFees, cardFeePassthroughEnabled)

    const platformTaxAmount = computePlatformFeeAmount({ platformFeeEntry, guestSubtotal, discountFactor, commissionAddOns })
    const customFeeBase = (guestSubtotal + customTaxableAddOnsTotal) * discountFactor
    const otherFeeItems = buildOtherFeeItems({ otherFees, customFeeBase })
    const feeContributions = [{ fee: platformFeeEntry, amount: platformTaxAmount }, ...otherFeeItems]

    let hiddenFeesTotal = 0
    for (const { fee, amount } of feeContributions) {
      if (amount <= 0) continue
      total += amount
      if (fee.show_as_line_item) {
        breakdown.push(buildLine({ kind: "fee", label: fee.title, value: amount }))
      } else {
        hiddenFeesTotal += amount
      }
    }

    cardProcessingFee = computeCardProcessingFee({ cardFeeEntry, total, depositFraction, cardFeeShare })
    total += cardProcessingFee

    breakdown.push(...buildFeesBreakdown({ hiddenFeesTotal, cardFeeEntry, cardProcessingFee, isCardFeeListed }))
  }

  return { total, guestSubtotal, addOnsTotal, discountAmount, breakdown, preCardTotal: total - cardProcessingFee }
}

/**
 * Prices one widget booking.
 *
 * @param {object} params
 * @param {object} params.priceData            the selected trip price (trip_prices[] entry)
 * @param {string} params.date                 booking (start) date, YYYY-MM-DD; adjustments use it
 * @param {object} params.counts               { main: { adults, children, infants, passengers } }
 * @param {object} params.addOnSelections      { [addOnId]: qty }
 * @param {Array}  params.tripAddOns           the trip's add_ons
 * @param {Array}  params.dayAdjustments       experience.day_adjustments
 * @param {Array}  params.seasonalAdjustments  experience.seasonal_adjustments
 * @param {Array}  params.availableDateRanges  experience.available_date_ranges
 * @param {Array}  params.customFees           experience.assigned_custom_fees
 * @param {Array}  params.discountPromos       applied promo codes
 * @param {boolean} params.cardFeePassthroughEnabled  platform_fee_setting.card_fee_passthrough_enabled
 * @param {number} params.depositFraction      share of the pre-card total paid by card now (getCardFeeFraction)
 * @param {number} params.cardFeeShare         this booking's share of the card fee's fixed part: 1 alone, a share in an order
 * @param {boolean} params.isCardFeeListed     false keeps the processing fee out of the breakdown (shown below the Total)
 * @param {object} params.multiDay             a multi-day booking's dates and rate kind (getMultiDayBooking); null for single-day
 * @param {object} params.progressivePricing   experience's progressive pricing (getProgressivePricing); none for the price before adjustments
 * @returns {{ total, guestSubtotal, addOnsTotal, discountAmount, breakdown, preCardTotal }}
 */
export const calculateWidgetBookingTotal = ({
  priceData = {},
  date,
  counts,
  addOnSelections = {},
  tripAddOns = [],
  dayAdjustments,
  seasonalAdjustments,
  availableDateRanges,
  customFees = [],
  discountPromos = [],
  cardFeePassthroughEnabled = false,
  depositFraction = 1,
  cardFeeShare = 1,
  isCardFeeListed = true,
  multiDay = null,
  progressivePricing = null
}) => {
  const { pricing_type, included_guests, max_guests } = priceData
  const includedGuests = included_guests ?? max_guests ?? 0

  // Fold any matching day adjustments (e.g. weekend surcharges) and seasonal adjustments
  // (e.g. a holiday range) directly into the base unit prices before computing totals, so they
  // apply to the flat rate / per-person rates and to extra-guest per-person rates alike.
  // Both kinds stack: each is computed off the base price and the deltas are summed. A multi-day
  // booking's unit prices cover the whole trip (computeMultiDayRates).
  const pricingTrip = multiDay && buildMultiDayPricingTrip({ multiDay, dayAdjustments, seasonalAdjustments, availableDateRanges, progressivePricing })
  const rates = multiDay
    ? computeMultiDayRates(priceData, multiDay, pricingTrip)
    : computeAdjustedRates(priceData, getApplicableAdjustments({ dayAdjustments, seasonalAdjustments, availableDateRanges, date }))

  const totalCounts = counts?.main || EMPTY_COUNTS

  const guestPricing = computeGuestPricingBreakdown({
    pricingType: pricing_type,
    totalCounts,
    rates,
    includedGuests
  })
  const progressiveDelta = computeProgressiveFixedDelta({ pricingTrip, priceData, multiDay, totalCounts })
  const guestTotal = progressiveDelta ? Math.max(0, guestPricing.total + progressiveDelta) : guestPricing.total
  const guestBreakdown = foldProgressiveDeltaIntoBreakdown(guestPricing.breakdown, progressiveDelta)
  return addAddOnsDiscountAndFees({
    guestTotal,
    guestBreakdown,
    addOnSelections,
    tripAddOns,
    customFees,
    discountPromos,
    cardFeePassthroughEnabled,
    depositFraction,
    cardFeeShare,
    isCardFeeListed
  })
}

const getApplicableTicketAdjustments = ({ dayAdjustments, seasonalAdjustments, availableDateRanges, date, ticketTypeId }) => [
  ...getApplicableDayAdjustments(dayAdjustments, date, ticketTypeId),
  ...getApplicableSeasonalAdjustments(seasonalAdjustments, date, availableDateRanges, ticketTypeId)
]

// Price of each ticket type on `date`: its base price plus the day and seasonal adjustments that
// apply to that type (every adjustment for all tickets, and those that selected it), summed off
// the base like private prices. Without adjustments these are the base prices.
// { [ticketTypeId]: price }
export const getTicketRates = ({ ticketTypes = [], date, dayAdjustments, seasonalAdjustments, availableDateRanges }) =>
  Object.fromEntries(
    ticketTypes.map((ticketType) => [
      ticketType.id,
      applyPriceAdjustments(
        ticketType.price,
        getApplicableTicketAdjustments({ dayAdjustments, seasonalAdjustments, availableDateRanges, date, ticketTypeId: ticketType.id })
      )
    ])
  )

/**
 * Prices one ticketed booking: a line per ticket type ("ticket" kind, with ticketTypeId and
 * title), then add-ons, discount and fees exactly as calculateWidgetBookingTotal does.
 *
 * @param {object} params
 * @param {Array}  params.ticketTypes   the trip's ticket_types ({ id, title, price })
 * @param {object} params.ticketCounts  { [ticketTypeId]: count }
 * @param {number} params.overrideGuestSubtotal  a guide's adjusted subtotal: the tickets' price the
 *   add-ons, discount and fees are worked out on, in place of the tickets' own (guest side never sets it)
 * The other params are calculateWidgetBookingTotal's.
 * @returns {{ total, guestSubtotal, addOnsTotal, discountAmount, breakdown, preCardTotal }}
 */
export const calculateTicketedBookingTotal = ({
  ticketTypes = [],
  ticketCounts = {},
  date,
  addOnSelections = {},
  tripAddOns = [],
  dayAdjustments,
  seasonalAdjustments,
  availableDateRanges,
  customFees = [],
  discountPromos = [],
  cardFeePassthroughEnabled = false,
  depositFraction = 1,
  cardFeeShare = 1,
  isCardFeeListed = true,
  overrideGuestSubtotal
}) => {
  const rates = getTicketRates({ ticketTypes, date, dayAdjustments, seasonalAdjustments, availableDateRanges })
  let guestTotal = 0
  const guestBreakdown = []
  for (const ticketType of ticketTypes) {
    const count = Number(ticketCounts?.[ticketType.id] || 0)
    if (count > 0) {
      const value = count * rates[ticketType.id]
      guestTotal += value
      guestBreakdown.push(
        buildLine({ kind: "ticket", label: `${ticketType.title} x ${count}`, value, count, ticketTypeId: ticketType.id, title: ticketType.title })
      )
    }
  }
  return addAddOnsDiscountAndFees({
    guestTotal: overrideGuestSubtotal ?? guestTotal,
    guestBreakdown,
    addOnSelections,
    tripAddOns,
    customFees,
    discountPromos,
    cardFeePassthroughEnabled,
    depositFraction,
    cardFeeShare,
    isCardFeeListed
  })
}

// Deposit / balance split shown under the total. The card fee is collected up front with the
// deposit, so the remaining balance carries no card fee.
export const computeDepositSplit = ({ preCardTotal = 0, cardProcessingFee = 0, depositPercent = 0 }) => {
  const safePreCardTotal = Math.max(0, preCardTotal)
  const depositBase = (safePreCardTotal * (depositPercent || 0)) / 100
  return {
    depositAmount: depositBase + Math.max(0, cardProcessingFee),
    balanceAmount: safePreCardTotal - depositBase
  }
}

// The card fee's fixed part ($0.30, or $0.49 for a PayPal-only guide) is charged once per order
// (client, Q33). Each booking of an order carries a share of it in proportion to its pre-card total,
// so the bookings' card fees add up to one fee on the order. Shares of the fixed fee, in the
// bookings' order.
export const getFixedCardFeeShares = (preCardTotals) => {
  const amounts = preCardTotals.map((value) => Math.max(0, Number(value) || 0))
  const sum = amounts.reduce((total, value) => total + value, 0)
  return amounts.map((value) => (sum > 0 ? value / sum : 1 / amounts.length))
}
